/**
 * Caption Limit Validation Util
 *
 * Single source of truth for the "caption too long" check shared between:
 * - The CTA disable-button guard (PostCreator/VariationCreator getDisabledReason())
 * - The submit-handler re-validation guard (usePostComposer/useVariationComposer
 *   handlePrimaryAction) — belt-and-suspenders so the composer can never fire a
 *   schedule/shoot mutation with an over-limit caption even if the disabled
 *   button is somehow bypassed (programmatic trigger, race, keyboard shortcut).
 *
 * Both call sites MUST use this helper (or an equivalent `checkLimitsWithCounting`
 * call against the same platform list) so the two guards never disagree.
 */

import { checkLimitsWithCounting } from '@/constants/platformLimits';
import type { PlatformOptions } from '@/types/scheduled-instances';

/**
 * Backend error code returned when a caption exceeds a platform's max length.
 * See backend/src/services/post.service.ts and scheduled-instance.service.ts
 * (`throw new AppError(..., 400, true, 'caption_too_long')`).
 */
export const CAPTION_TOO_LONG_ERROR_CODE = 'caption_too_long';

export interface CaptionLimitCheckResult {
  /** True if the caption fits within every selected platform's limit */
  isWithinAll: boolean;
  /** Comma-separated display names of platforms the caption exceeds (empty string if isWithinAll) */
  overPlatformNames: string;
}

/**
 * Per-platform text overrides for {@link checkCaptionWithinLimits}. Only platforms that
 * actually publish an alternate field instead of the shared caption belong here.
 *
 * YouTube publishes a per-channel DESCRIPTION override (see the "Description" field in
 * PlatformOptionsSection.tsx, `channelOptions[channelId].youtube.description`) — NOT the
 * caption — whenever one is set. If omitted/undefined, YouTube falls back to the caption,
 * same as every other platform.
 */
export interface CaptionLimitTextOverrides {
  youtube?: string;
}

/**
 * Check a caption against the character limits of the given platforms.
 * Uses platform-aware character counting (e.g. Twitter/X counts emoji as 2 chars),
 * mirroring the exact `checkLimitsWithCounting(caption, platforms, 'characters')`
 * call already used by getDisabledReason() in PostCreator/VariationCreator.
 *
 * `textOverrides` lets specific platforms be validated against a different effective
 * text than the shared caption (currently only YouTube, whose description override
 * — if set — is what actually gets published, not the caption). Any platform without
 * a matching override entry (or with an empty/undefined one) is validated against
 * `caption` as before.
 */
export function checkCaptionWithinLimits(
  caption: string,
  platforms: string[],
  textOverrides?: CaptionLimitTextOverrides
): CaptionLimitCheckResult {
  const effectivePlatforms = platforms.length > 0 ? platforms : ['linkedin'];
  const overLimits: string[] = [];
  let isWithinAll = true;

  for (const platform of effectivePlatforms) {
    const normalized = platform.toLowerCase();
    const override = normalized === 'youtube' ? textOverrides?.youtube : undefined;
    const effectiveText = override || caption;

    const charCheck = checkLimitsWithCounting(effectiveText, [normalized], 'characters');
    if (!charCheck.isWithinAll) {
      isWithinAll = false;
      overLimits.push(...charCheck.overLimits.map((o) => o.displayName));
    }
  }

  return {
    isWithinAll,
    overPlatformNames: overLimits.join(', '),
  };
}

/**
 * Resolve the effective text YouTube will actually publish for the currently selected
 * channels — the per-channel description override if one is set, else the caption —
 * for use as the `youtube` entry of {@link CaptionLimitTextOverrides}.
 *
 * A post can target multiple YouTube channels at once, each with its own optional
 * description override. Returns the *longest* per-channel effective text (worst case)
 * so the limit check catches an over-limit description/caption on ANY selected YouTube
 * channel, not just the first one. Returns `undefined` when no YouTube channel is
 * selected (nothing to override).
 */
export function getYoutubeEffectiveCaptionText(
  caption: string,
  channelIds: number[],
  channelOptions: Record<number, PlatformOptions>,
  channels: Array<{ id: number; platform: string }> | undefined
): string | undefined {
  if (!channels) return undefined;

  const youtubeChannelIds = channels
    .filter((ch) => channelIds.includes(ch.id) && ch.platform.toLowerCase() === 'youtube')
    .map((ch) => ch.id);

  if (youtubeChannelIds.length === 0) return undefined;

  let worstCase: string | undefined;
  for (const channelId of youtubeChannelIds) {
    const effectiveText = channelOptions[channelId]?.youtube?.description || caption;
    if (worstCase === undefined || effectiveText.length > worstCase.length) {
      worstCase = effectiveText;
    }
  }
  return worstCase;
}
